import React, { useCallback } from "react";
import { log } from "@qgiv/core-js";
import { useDispatch } from "react-redux";
import { getIsOnePaySubmission } from "@qgiv/donation-form";
import { ModalContentReplacerEnums } from "../../common/ModalContentReplacer";
import { usePaymentPageData } from "../../PageDataHooks";
import {
    getInitialTouched,
    getInitialValues,
    getValidationSchema,
} from "./formik";
import { setShouldScrollToTop } from "../../../redux/slices/appSettingsSlice";
import {
    updateSubmitDonation,
    updateValidateDonation,
} from "../../../redux/slices/donationDetailsSlice";
import ConnectedRecurringUpgrade from "./RecurringUpgrade";
import PaymentPage from "./PaymentPage";
import ConnectedExitIntent from "../../common/ExitIntent";

const ConnectedPaymentPage = () => {
    // -------------------------------------------------------------------------
    // NOTE: Single Step form uses a different component set than these Connected Pages.
    // It does share the custom getPageData hooks, but if you make changes in the Connected Pages
    // make sure to make the same changes in the ConnectedSinglePage.js file.
    // -------------------------------------------------------------------------
    const dispatch = useDispatch();
    const paymentPageData = usePaymentPageData();
    const {
        acceptedEmailReminders,
        documentTitle,
        enableAbandonedGifts,
        enableReminderEmails,
        formikSettings,
        getShouldSubmitDonation,
        hasOnePay,
        selectedContentToDisplay,
        shouldDisplayRecurringUpgrade,
        submitDonation,
        validateOnePayDonation,
    } = paymentPageData;

    const getAlternateContent = useCallback(() => {
        const alternateContent = [];
        if (shouldDisplayRecurringUpgrade) {
            alternateContent.push({
                component: <ConnectedRecurringUpgrade />,
                type: ModalContentReplacerEnums.UPGRADE_RECURRING_UI,
            });
        }
        if (enableAbandonedGifts && enableReminderEmails) {
            alternateContent.push({
                component: <ConnectedExitIntent />,
                type: ModalContentReplacerEnums.EXIT_INTENT_UI,
            });
        }
        return alternateContent;
    }, [
        enableAbandonedGifts,
        enableReminderEmails,
        shouldDisplayRecurringUpgrade,
    ]);

    const alternateContent = getAlternateContent();

    // -------------------------------------------------------------------------
    // NOTE: What to do about current (original) submission process:
    // I don't want to mess around with the current logic for submitting
    // donations with the PCI iframe, it's pretty tightly coupled. Basically, if we are
    // using the original submission flow, we just want to call the original getShouldSubmitDonation.
    // A major issue for the One Pay submission flow is going to be how we want to handle recurring upgrade.
    // I do not believe we will be able to know what the state of the payment data entered
    // on the One Pay component wrapper / iframe is going to be. What I think I should do for now to keep moving
    // is group One Pay with PayPal/ApplePay where we do not allow recurring upgrade to display.
    // If we have more information available to us, this might change.
    // -------------------------------------------------------------------------
    const onSubmit = (values, formikBag) => {
        const isOnePaySubmission = getIsOnePaySubmission({
            hasOnePay,
            values,
        });

        // Verify that the conditions are right before proceeding to submission
        if (isOnePaySubmission) {
            // we are not showing recurring upgrade for now
            dispatch(updateValidateDonation(true));
        } else {
            getShouldSubmitDonation(values, formikBag)
                .then(({ shouldSubmitDonation }) => {
                    if (shouldSubmitDonation) {
                        dispatch(setShouldScrollToTop(true));
                        dispatch(updateSubmitDonation(true));
                    }
                })
                .catch((errors) => {
                    log(errors);
                });
        }
    };

    const {
        initialTouchedSettings,
        initialValuesSettings,
        validationSettings,
    } = formikSettings;

    const formikProps = {
        initialTouched: getInitialTouched(initialTouchedSettings),
        initialValues: getInitialValues(initialValuesSettings),
        validationSchema: getValidationSchema(validationSettings),
        onSubmit,
    };

    const paymentPageProps = {
        acceptedEmailReminders,
        alternateContent,
        documentTitle,
        formikProps,
        selectedContentToDisplay,
        submitDonation,
        validateOnePayDonation,
    };
    return <PaymentPage {...paymentPageProps} />;
};

export default ConnectedPaymentPage;
